import { observer } from "mobx-react-lite";
import { type CSSProperties, useMemo } from "react";

import { LazyRichTextEditor } from "../../lazy-rich-text-editor";
import { RichTextContent } from "../../rich-text-content";
import { richTextToPlainText } from "../../sanitize-rich-text";
import { CellExpandableLine } from "./cell-expandable-line";

export const CellRichText = observer(function CellRichText<T>(props: {
  item: T;
  attr: keyof T;
  /** Draft row while editing; the edited attr holds the editor's HTML string. */
  patch?: Partial<Record<keyof T, unknown>>;
  displayValue?: unknown;
  disabled?: boolean;
  /** Column width in px; caps the cell so long content can't stretch the column. */
  width?: number;
}) {
  const editing = !!props.patch && !props.disabled;
  // The table is `table-layout: fixed` but `width: max-content` with
  // `td { max-width: none }`, so a column grows to its content's intrinsic width.
  // Capping the cell content at the column width bounds that contribution.
  const style = props.width != null ? { maxWidth: props.width } : undefined;

  if (editing && props.patch) {
    const patch = props.patch;
    return (
      // Reset the column's inherited text-align (cells default to centered) so
      // the editor content reads left-aligned like a normal text field.
      <div className="text-left" style={style}>
        <LazyRichTextEditor
          toolbar={["text-formatting"]}
          value={String(patch[props.attr] ?? "")}
          onChange={(v) => {
            patch[props.attr] = v;
          }}
        />
      </div>
    );
  }

  const raw =
    props.displayValue !== undefined
      ? props.displayValue
      : props.item[props.attr];
  return <RichTextStatic value={String(raw ?? "")} style={style} />;
});

// The collapsed line is the value's plain text; its formatting survives only
// in the popover.
function RichTextStatic({
  value,
  style,
}: {
  value: string;
  style?: CSSProperties;
}) {
  // Memoized: the observer parent re-renders on any row change, and parsing
  // HTML per cell adds up in tables that render every row at once.
  const text = useMemo(() => richTextToPlainText(value), [value]);

  return (
    // Handing over the extracted text (not the raw value) also treats markup
    // with no visible content (e.g. "<p></p>") as empty, hiding the expand
    // button.
    <CellExpandableLine
      line={text}
      style={style}
      popoverClassName="max-h-80 w-96 max-w-[90vw] overflow-y-auto"
    >
      {/* Rendering here (content mounts only while open) keeps the cost
          of the full-HTML sanitize pass out of the table's render path. */}
      <RichTextContent value={value} className="prose prose-sm max-w-full" />
    </CellExpandableLine>
  );
}
